<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const loading = ref(true)
const error = ref('')
const content = ref('')
const host = ref(null)
let pageStyleNodes = []
let searchData = []
let searchTimer

const internalOrigin = window.location.origin
const legacyHosts = new Set(['mcpcn.com', 'www.mcpcn.com'])

function applyBranding(doc) {
  const replacements = [
    ['MCP 中文站（Model Context Protocol 中文）', 'MCP Club 中文站'],
    ['MCP中文站（Model Context Protocol 中文）', 'MCP Club 中文站'],
    ['MCP 中文站', 'MCP Club 中文站'],
    ['MCP中文站', 'MCP Club 中文站'],
    ['MCPcn.com', 'mcpclub.cn'],
    ['mcpcn.com', 'mcpclub.cn'],
  ]
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const textNodes = []
  while (walker.nextNode()) textNodes.push(walker.currentNode)
  textNodes.forEach((node) => {
    let value = node.nodeValue || ''
    replacements.forEach(([from, to]) => { value = value.replaceAll(from, to) })
    node.nodeValue = value
  })
  doc.querySelectorAll('img[alt], [title]').forEach((element) => {
    for (const attribute of ['alt', 'title']) {
      if (!element.hasAttribute(attribute)) continue
      let value = element.getAttribute(attribute) || ''
      replacements.forEach(([from, to]) => { value = value.replaceAll(from, to) })
      element.setAttribute(attribute, value)
    }
  })

  const badge = [...doc.querySelectorAll('span')].find((element) => element.textContent?.includes('开放标准 · 安全可扩展'))
  const home = badge?.closest('a')?.parentElement
  if (home) {
    home.classList.add('mcp-hub-home')
    const headlineWrap = home.querySelector('h1')?.parentElement
    const descriptionWrap = headlineWrap?.nextElementSibling
    const actions = descriptionWrap?.nextElementSibling
    const spacer = actions?.nextElementSibling
    badge.closest('a')?.classList.add('hub-eyebrow')
    headlineWrap?.classList.add('hub-headline')
    descriptionWrap?.classList.add('hub-description')
    actions?.classList.add('hub-actions')
    spacer?.classList.add('hub-spacer')
    const headline = headlineWrap?.querySelector('h1')
    if (headline) headline.innerHTML = '让模型连接<br>工具与真实世界<span>MCP Club 中文知识站</span>'
    const description = descriptionWrap?.querySelector('p')
    if (description) description.innerHTML = '面向开发者与团队的 MCP 知识中枢。<br>从协议原理到生产实践，在一个站点完成学习、查询与落地。'
    const quickstartTitle = home.querySelector('.offline-quickstart h2')
    if (quickstartTitle) quickstartTitle.textContent = '选择你的入口'

    const visual = doc.createElement('div')
    visual.className = 'hub-protocol-map not-prose'
    visual.setAttribute('aria-hidden', 'true')
    visual.innerHTML = `
      <div class="hub-map-label"><span></span> LIVE PROTOCOL MAP</div>
      <div class="hub-map-stage">
        <div class="hub-map-ring hub-map-ring-a"></div>
        <div class="hub-map-ring hub-map-ring-b"></div>
        <i class="hub-map-line hub-map-line-a"></i>
        <i class="hub-map-line hub-map-line-b"></i>
        <i class="hub-map-line hub-map-line-c"></i>
        <div class="hub-map-core"><small>OPEN PROTOCOL</small><strong>MCP</strong><em>CONNECTED</em></div>
        <div class="hub-map-node hub-map-model"><b>MODEL</b><span>推理与采样</span></div>
        <div class="hub-map-node hub-map-tools"><b>TOOLS</b><span>能力与执行</span></div>
        <div class="hub-map-node hub-map-data"><b>DATA</b><span>上下文与资源</span></div>
      </div>`
    home.insertBefore(visual, spacer || home.firstChild)
  }

  doc.querySelectorAll('aside h3').forEach((heading) => {
    const label = heading.textContent?.trim() || ''
    if (!label.includes('分类') && !label.includes('标签')) return
    const section = heading.parentElement
    const chips = heading.nextElementSibling
    section?.classList.add('blog-taxonomy', label.includes('分类') ? 'blog-categories' : 'blog-tags')
    heading.classList.add('blog-taxonomy-title')
    chips?.classList.add('blog-taxonomy-chips')
    chips?.querySelectorAll(':scope > a, :scope > span').forEach((chip) => chip.classList.add('blog-taxonomy-chip'))
  })

  const footer = doc.querySelector('footer')
  if (footer) {
    footer.classList.add('mcp-hub-footer')
    footer.querySelector('nav')?.classList.add('mcp-footer-links')
    const powered = [...footer.querySelectorAll('a')].find((anchor) => anchor.textContent?.includes('驱动'))
    if (powered) {
      powered.classList.add('mcp-footer-brand')
      powered.removeAttribute('href')
      powered.removeAttribute('target')
      powered.removeAttribute('rel')
      powered.removeAttribute('title')
      powered.innerHTML = '<strong>MCP Club 中文站</strong><span>mcpclub.cn</span>'
      powered.closest('footer')?.querySelector(':scope > div:last-child')?.classList.add('mcp-footer-bottom')
    }
    const copyright = [...footer.querySelectorAll('div')].find((element) => element.textContent?.trim().startsWith('©'))
    if (copyright) copyright.textContent = '© 2026 mcpclub.cn · Model Context Protocol 中文知识站'
  }
}

function routeToSource(pathname = window.location.pathname) {
  let route = decodeURI(pathname).replace(/^\/+/, '')
  if (!route) route = 'index.html'
  else if (route.endsWith('/')) route += 'index.html'
  else if (!/\.[a-z0-9]+$/i.test(route)) route += '/index.html'
  return `/content/${route}`
}

function routeFromUrl(url) {
  const parsed = new URL(url, window.location.href)
  if (parsed.origin !== internalOrigin || parsed.pathname.startsWith('/content/')) return null
  let path = parsed.pathname
  if (path.endsWith('/index.html')) path = path.slice(0, -10) || '/'
  else if (path.endsWith('.html')) path = path.slice(0, -5)
  return `${path}${parsed.search}${parsed.hash}`
}

function absolutizeDocument(doc, sourceUrl) {
  const attrs = ['href', 'src', 'poster']
  doc.body.querySelectorAll('*').forEach((element) => {
    attrs.forEach((attr) => {
      const value = element.getAttribute(attr)
      if (!value || value.startsWith('#') || /^(data:|mailto:|tel:|javascript:)/i.test(value)) return
      try {
        const resolved = new URL(value, sourceUrl)
        if (legacyHosts.has(resolved.hostname)) {
          resolved.protocol = window.location.protocol
          resolved.host = window.location.host
        }
        if (resolved.origin === internalOrigin && !resolved.pathname.startsWith('/content/')) resolved.pathname = `/content${resolved.pathname}`
        element.setAttribute(attr, resolved.href)
      } catch { /* keep malformed legacy URLs untouched */ }
    })
    const srcset = element.getAttribute('srcset')
    if (srcset) {
      element.setAttribute('srcset', srcset.split(',').map((part) => {
        const [url, descriptor] = part.trim().split(/\s+/, 2)
        try {
          const resolved = new URL(url, sourceUrl)
          if (resolved.origin === internalOrigin && !resolved.pathname.startsWith('/content/')) resolved.pathname = `/content${resolved.pathname}`
          return `${resolved.href}${descriptor ? ` ${descriptor}` : ''}`
        } catch { return part }
      }).join(', '))
    }
  })

  doc.body.querySelectorAll('a[href]').forEach((anchor) => {
    try {
      const resolved = new URL(anchor.getAttribute('href'), sourceUrl)
      if (resolved.origin !== internalOrigin) {
        anchor.removeAttribute('href')
        anchor.removeAttribute('target')
        anchor.removeAttribute('rel')
        anchor.classList.add('vue-external-disabled')
        anchor.setAttribute('aria-disabled', 'true')
        anchor.setAttribute('title', '外部链接已停用')
      }
    } catch { /* malformed legacy links remain inert */ }
  })
}

function syncHead(doc, sourceUrl) {
  pageStyleNodes.forEach((node) => node.remove())
  pageStyleNodes = []
  document.title = (doc.title || 'MCP Club 中文站')
    .replaceAll('MCP 中文站（Model Context Protocol 中文）', 'MCP Club 中文站')
    .replaceAll('MCP中文站（Model Context Protocol 中文）', 'MCP Club 中文站')
    .replaceAll('MCP 中文站', 'MCP Club 中文站')
    .replaceAll('MCP中文站', 'MCP Club 中文站')
  document.documentElement.lang = doc.documentElement.lang || 'zh-CN'
  document.documentElement.dir = doc.documentElement.dir || 'ltr'

  doc.head.querySelectorAll('link[rel="stylesheet"], style').forEach((source) => {
    const node = source.cloneNode(true)
    node.dataset.vuePageAsset = 'true'
    if (node.tagName === 'LINK') {
      const href = new URL(node.getAttribute('href'), sourceUrl)
      if (href.origin === internalOrigin && !href.pathname.startsWith('/content/')) href.pathname = `/content${href.pathname}`
      node.href = href.href
    }
    document.head.appendChild(node)
    pageStyleNodes.push(node)
  })

  const description = doc.querySelector('meta[name="description"]')?.content
  if (description) document.querySelector('meta[name="description"]')?.setAttribute('content', description)
}

async function loadPage({ push = false, url = window.location.href } = {}) {
  const destination = new URL(url, window.location.href)
  if (push) history.pushState({}, '', destination.href)
  loading.value = true
  error.value = ''
  try {
    const sourcePath = routeToSource(destination.pathname)
    const response = await fetch(sourcePath)
    if (!response.ok) throw new Error(`页面加载失败（${response.status}）`)
    const html = await response.text()
    const doc = new DOMParser().parseFromString(html, 'text/html')
    applyBranding(doc)
    absolutizeDocument(doc, new URL(sourcePath, internalOrigin))
    syncHead(doc, new URL(sourcePath, internalOrigin))
    doc.body.querySelectorAll('script').forEach((script) => script.remove())
    content.value = doc.body.innerHTML
    await nextTick()
    wireInteractions()
    if (destination.hash) document.getElementById(decodeURIComponent(destination.hash.slice(1)))?.scrollIntoView()
    else window.scrollTo({ top: 0 })
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '页面加载失败'
  } finally {
    loading.value = false
  }
}

function setTheme(theme) {
  const resolved = theme === 'system'
    ? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(resolved)
  document.documentElement.style.colorScheme = resolved
  localStorage.setItem('color-theme', theme)
}

function toggleTheme() {
  setTheme(document.documentElement.classList.contains('dark') ? 'light' : 'dark')
}

function toggleMenu() {
  const sidebar = host.value?.querySelector('.hextra-sidebar-container')
  if (!sidebar) return
  const open = sidebar.classList.toggle('vue-mobile-open')
  document.body.classList.toggle('vue-menu-open', open)
}

function toggleSidebarItem(button) {
  const link = button.closest('a')
  const panel = link?.nextElementSibling
  if (!panel) return false
  panel.classList.toggle('vue-collapsed')
  button.classList.toggle('vue-expanded')
  return true
}

async function copyCode(button) {
  const block = button.closest('.hextra-code-block, pre')?.querySelector('code') || button.closest('pre')?.querySelector('code')
  if (!block) return
  await navigator.clipboard.writeText(block.textContent || '')
  const old = button.getAttribute('aria-label')
  button.setAttribute('aria-label', '已复制')
  setTimeout(() => button.setAttribute('aria-label', old || '复制代码'), 1200)
}

function languageForPath() {
  return ['en', 'de', 'fr', 'ko', 'zh-tw'].find((code) => location.pathname === `/${code}` || location.pathname.startsWith(`/${code}/`)) || 'zh-cn'
}

async function ensureSearchData() {
  const language = languageForPath()
  if (searchData.language === language) return searchData.items
  try {
    const response = await fetch(`/content/${language}.search-data.json`)
    const raw = await response.json()
    const sourceItems = Array.isArray(raw) ? raw : (raw.items || raw.data || raw)
    const items = Array.isArray(sourceItems) ? sourceItems : Object.entries(sourceItems).map(([url, page]) => ({
      url,
      title: page?.title || url,
      content: page?.content || Object.values(page?.data || {}).join(' '),
    }))
    searchData = { language, items }
    return items
  } catch {
    searchData = { language, items: [] }
    return []
  }
}

async function runSearch(input) {
  const wrapper = input.closest('.hextra-search-wrapper')
  const list = wrapper?.querySelector('.hextra-search-results')
  if (!list) return
  const query = input.value.trim().toLocaleLowerCase()
  if (!query) {
    list.classList.add('hx:hidden')
    list.replaceChildren()
    return
  }
  const items = await ensureSearchData()
  const results = items.filter((item) => {
    const haystack = `${item.title || ''} ${item.content || item.description || ''}`.toLocaleLowerCase()
    return haystack.includes(query)
  }).slice(0, 12)
  list.replaceChildren(...results.map((item) => {
    const li = document.createElement('li')
    const a = document.createElement('a')
    a.className = 'vue-search-result'
    a.href = item.uri || item.url || item.permalink || '#'
    a.innerHTML = `<strong>${escapeHtml(item.title || '未命名页面')}</strong><span>${escapeHtml((item.content || item.description || '').replace(/<[^>]+>/g, '').slice(0, 100))}</span>`
    li.appendChild(a)
    return li
  }))
  if (!results.length) {
    const li = document.createElement('li')
    li.className = 'vue-search-empty'
    li.textContent = '没有找到相关内容'
    list.appendChild(li)
  }
  list.classList.remove('hx:hidden')
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[char]))
}

function wireInteractions() {
  host.value?.querySelectorAll('details').forEach((details) => {
    details.querySelector('summary')?.setAttribute('role', 'button')
  })
}

function onClick(event) {
  const target = event.target instanceof Element ? event.target : null
  if (!target) return
  const hamburger = target.closest('.hextra-hamburger-menu')
  if (hamburger) {
    event.preventDefault()
    toggleMenu()
    return
  }
  const themeChoice = target.closest('.hextra-theme-toggle-options [data-item]')
  if (themeChoice) {
    event.preventDefault()
    setTheme(themeChoice.dataset.item || 'system')
    themeChoice.closest('.hextra-theme-toggle-options')?.classList.add('hx:hidden')
    return
  }
  const themeButton = target.closest('.hextra-theme-toggle, button[title*="主题"], button[aria-label*="theme" i]')
  if (themeButton) {
    event.preventDefault()
    const options = themeButton.parentElement?.querySelector('.hextra-theme-toggle-options')
    if (options) options.classList.toggle('hx:hidden')
    else toggleTheme()
    return
  }
  const languageButton = target.closest('.hextra-language-switcher')
  if (languageButton) {
    event.preventDefault()
    languageButton.parentElement?.querySelector('.hextra-language-options')?.classList.toggle('hx:hidden')
    return
  }
  const collapseButton = target.closest('.hextra-sidebar-collapsible-button')
  if (collapseButton && toggleSidebarItem(collapseButton)) {
    event.preventDefault()
    return
  }
  const copyButton = target.closest('.hextra-code-copy-btn, button[aria-label*="copy" i], button[title*="复制"]')
  if (copyButton) {
    event.preventDefault()
    copyCode(copyButton)
    return
  }
  const anchor = target.closest('a[href]')
  if (!anchor || anchor.target === '_blank' || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
  const raw = anchor.getAttribute('href')
  if (!raw || /^(mailto:|tel:|javascript:)/i.test(raw)) return
  const parsed = new URL(raw, window.location.href)
  if (parsed.pathname.startsWith('/content/')) parsed.pathname = parsed.pathname.slice(8) || '/'
  const route = routeFromUrl(parsed.href)
  if (route === null) return
  event.preventDefault()
  document.body.classList.remove('vue-menu-open')
  host.value?.querySelector('.hextra-sidebar-container')?.classList.remove('vue-mobile-open')
  loadPage({ push: true, url: route })
}

function onInput(event) {
  const input = event.target instanceof HTMLInputElement ? event.target.closest('.hextra-search-input') : null
  if (!input) return
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => runSearch(input), 120)
}

function onKeydown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    host.value?.querySelector('.hextra-search-input')?.focus()
  }
  if (event.key === 'Escape') {
    host.value?.querySelectorAll('.hextra-search-results').forEach((list) => list.classList.add('hx:hidden'))
    document.body.classList.remove('vue-menu-open')
    host.value?.querySelector('.hextra-sidebar-container')?.classList.remove('vue-mobile-open')
  }
}

function onPopState() { loadPage() }

onMounted(() => {
  setTheme(localStorage.getItem('color-theme') || 'system')
  document.addEventListener('click', onClick)
  document.addEventListener('input', onInput)
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('popstate', onPopState)
  loadPage()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClick)
  document.removeEventListener('input', onInput)
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('popstate', onPopState)
  pageStyleNodes.forEach((node) => node.remove())
})
</script>

<template>
  <div class="vue-app-shell">
    <div v-if="loading" class="vue-loading" role="status" aria-live="polite">
      <span class="vue-spinner" aria-hidden="true"></span>
      <span>正在加载页面…</span>
    </div>
    <div v-if="error" class="vue-error" role="alert">
      <strong>无法打开这个页面</strong>
      <p>{{ error }}</p>
      <button type="button" @click="loadPage()">重新加载</button>
    </div>
    <div ref="host" class="vue-page-host" :aria-busy="loading" v-html="content"></div>
  </div>
</template>
